import { TEXT_FILTER_OPERATORS, type TextFilterCondition } from '@cmoh/shared'
import type { DateFilterValue, FilterConfig, NumberFilterValue } from '../types/data-table.types'

/**
 * Type guard to check if a value is DateFilterValue
 */
export const isDateFilterValue = (value: unknown): value is DateFilterValue => {
  if (typeof value !== 'object' || value === null) return false

  const obj = value as Record<string, unknown>
  return (
    ('from' in obj || 'to' in obj) &&
    (obj.from === undefined || typeof obj.from === 'string') &&
    (obj.to === undefined || typeof obj.to === 'string')
  )
}

/**
 * Type guard to check if a value is NumberFilterValue
 */
export const isNumberFilterValue = (value: unknown): value is NumberFilterValue => {
  if (typeof value !== 'object' || value === null) return false

  const obj = value as Record<string, unknown>
  return (
    ('min' in obj || 'max' in obj) &&
    (obj.min === undefined || typeof obj.min === 'string') &&
    (obj.max === undefined || typeof obj.max === 'string')
  )
}

/**
 * Type guard to check if a value is `TextFilterCondition[]` — the shape
 * produced by `TextAdvancedFilter`.
 */
export const isTextFilterConditionArray = (value: unknown): value is TextFilterCondition[] => {
  if (!Array.isArray(value)) return false
  return value.every(
    (entry) =>
      typeof entry === 'object' &&
      entry !== null &&
      'operator' in entry &&
      'value' in entry &&
      typeof (entry as { value: unknown }).value === 'string' &&
      (TEXT_FILTER_OPERATORS as readonly string[]).includes(
        (entry as { operator: string }).operator,
      ),
  )
}

/**
 * Type guard to check if a value is FilterConfig
 */
export const isFilterConfig = (value: unknown): value is FilterConfig => {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const obj = value as Record<string, unknown>

  return (
    'filterable' in obj ||
    'filterType' in obj ||
    'filterTitle' in obj ||
    'filterPlaceholder' in obj ||
    'filterOptions' in obj ||
    'customFilter' in obj ||
    'validateFilter' in obj
  )
}

/**
 * Type guard to check if a columnDef has filterConfig
 */
export const hasFilterConfig = (
  columnDef: unknown,
): columnDef is { filterConfig?: FilterConfig } => {
  return typeof columnDef === 'object' && columnDef !== null && 'filterConfig' in columnDef
}

/**
 * Hook that provides type guards for filter values
 */
export function useFilterTypeGuards() {
  return {
    isFilterConfig,
    hasFilterConfig,
    isDateFilterValue,
    isNumberFilterValue,
    isTextFilterConditionArray,
  }
}
